<template>
  <div v-if="userParameters !== null">
    <Dialog
      :visible="modelValue"
      modal
      closable
      @update:visible="$emit('update:modelValue', $event)"
      :header="$t('c.fundingForms.vericheckDialog.paymentProfile')"
      :style="{ width: '95vw', maxWidth: '24rem' }"
      :breakpoints="{ '640px': '95vw' }"
    >
      <div class="dialog-container">
        <div class="mb-1 font-bold">
          {{ $t('c.fundingForms.vericheckDialog.routingNumberLabel') }}:
        </div>
        <InputText
          class="mb-2 w-full md:w-16rem"
          :value="routingNumber"
          inputId="routing-number"
          @input="validateRoutingNumber"
        />
        <div class="mb-1 font-bold">
          {{ $t('c.fundingForms.vericheckDialog.accountNumberLabel') }}:
        </div>
        <InputText
          class="mb-2 w-full md:w-16rem"
          :value="accountNumber"
          inputId="account-number"
          @input="validateAccountNumber"
          inputmode="numeric"
          maxlength="17"
        />
        <div style="margin: 10px 0px 20px 0px">
          <div class="mb-1 font-bold">
            {{ $t('c.fundingForms.vericheckDialog.accountTypeLabel') }}:
          </div>
          <div
            class="p-field-radiobutton"
            v-for="account in accountTypes"
            :key="account.value"
            style="margin-bottom: 10px"
          >
            <RadioButton
              v-model="selecedAccountType"
              :inputId="account.value"
              name="accountTypes"
              :value="account.value"
              class="mr-1"
            />
            <label :for="account.value">{{ account.label }}</label>
          </div>
        </div>
        <div class="mb-1 font-bold">{{ $t('c.fundingForms.vericheckDialog.firstNameLabel') }}:</div>
        <InputText
          class="mb-2 w-full md:w-16rem"
          v-model="userParameters.firstName"
          inputId="first-name"
          type="text"
          maxlength="25"
        />
        <div class="mb-1 font-bold">{{ $t('c.fundingForms.vericheckDialog.lastNameLabel') }}:</div>
        <InputText
          class="mb-2 w-full md:w-16rem"
          v-model="userParameters.lastName"
          inputId="last-name"
          type="text"
          maxlength="25"
        />
        <div class="mb-1 font-bold">{{ $t('c.fundingForms.vericheckDialog.addressLabel') }}:</div>
        <InputText
          class="mb-2 w-full md:w-16rem"
          v-model="userParameters.address1"
          inputId="address"
          type="text"
        />
        <div class="mb-1 font-bold">{{ $t('c.fundingForms.vericheckDialog.cityLabel') }}:</div>
        <InputText
          class="mb-2 w-full md:w-16rem"
          v-model="userParameters.city"
          inputId="city"
          type="text"
        />
        <div class="mb-1 font-bold">{{ $t('c.fundingForms.vericheckDialog.stateLabel') }}:</div>
        <Select
          v-model="userParameters.state"
          :options="states"
          optionLabel="label"
          optionValue="value"
          :placeholder="$t('c.fundingForms.vericheckDialog.selectAState')"
          filter
          class="mb-2 w-full md:w-16rem"
        />
        <div class="mb-1 font-bold">{{ $t('c.fundingForms.transitDialog.zipCodeLabel') }}:</div>
        <InputText
          class="mb-2 w-full md:w-16rem"
          v-model="userParameters.zipCode"
          inputId="zip-code"
          :useGrouping="false"
          style="width: 265px"
          maxlength="5"
          inputmode="numeric"
        />
      </div>
      <div style="margin: 25px 0 25px 25px">
        <Button
          :label="$t('c.fundingForms.vericheckDialog.saveLabel')"
          @click="save()"
          class="save-button w-8rem"
          raised
        />
        <Button
          :label="$t('c.fundingForms.vericheckDialog.cancelLabel')"
          @click="$emit('update:modelValue', false)"
          text
          raised
          class="ml-5 w-8rem"
        />
      </div>
    </Dialog>
  </div>
</template>

<script lang="ts">
import { API } from '@/modules/api'
import type { UserData } from '@/modules/api/get/userData'

export interface AccountTypeOption {
  label: string
  value: string
}

export default {
  name: 'VericheckDialog',
  components: {},

  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    districtId: {
      type: Number,
      required: true
    },
    environmentId: {
      type: [Number, null],
      required: true
    },
    initialUserData: {
      type: [Object, null] as unknown as () => UserData | null,
      default: null
    },
    saveHandler: {
      type: [Function, null] as unknown as () => ((payload: object) => Promise<void>) | null,
      default: null
    }
  },

  computed: {},

  data() {
    return {
      userParameters: null as UserData | null,
      routingNumber: null,
      accountNumber: null,
      accountTypes: [] as AccountTypeOption[],
      selecedAccountType: 'checking',
      states: [
        { label: 'Alabama', value: 'AL' },
        { label: 'Alaska', value: 'AK' },
        { label: 'Arizona', value: 'AZ' },
        { label: 'Arkansas', value: 'AR' },
        { label: 'California', value: 'CA' },
        { label: 'Colorado', value: 'CO' },
        { label: 'Connecticut', value: 'CT' },
        { label: 'Delaware', value: 'DE' },
        { label: 'District of Columbia', value: 'DC' },
        { label: 'Florida', value: 'FL' },
        { label: 'Georgia', value: 'GA' },
        { label: 'Hawaii', value: 'HI' },
        { label: 'Idaho', value: 'ID' },
        { label: 'Illinois', value: 'IL' },
        { label: 'Indiana', value: 'IN' },
        { label: 'Iowa', value: 'IA' },
        { label: 'Kansas', value: 'KS' },
        { label: 'Kentucky', value: 'KY' },
        { label: 'Louisiana', value: 'LA' },
        { label: 'Maine', value: 'ME' },
        { label: 'Maryland', value: 'MD' },
        { label: 'Massachusetts', value: 'MA' },
        { label: 'Michigan', value: 'MI' },
        { label: 'Minnesota', value: 'MN' },
        { label: 'Mississippi', value: 'MS' },
        { label: 'Missouri', value: 'MO' },
        { label: 'Montana', value: 'MT' },
        { label: 'Nebraska', value: 'NE' },
        { label: 'Nevada', value: 'NV' },
        { label: 'New Hampshire', value: 'NH' },
        { label: 'New Jersey', value: 'NJ' },
        { label: 'New Mexico', value: 'NM' },
        { label: 'New York', value: 'NY' },
        { label: 'North Carolina', value: 'NC' },
        { label: 'North Dakota', value: 'ND' },
        { label: 'Ohio', value: 'OH' },
        { label: 'Oklahoma', value: 'OK' },
        { label: 'Oregon', value: 'OR' },
        { label: 'Pennsylvania', value: 'PA' },
        { label: 'Rhode Island', value: 'RI' },
        { label: 'South Carolina', value: 'SC' },
        { label: 'South Dakota', value: 'SD' },
        { label: 'Tennessee', value: 'TN' },
        { label: 'Texas', value: 'TX' },
        { label: 'Utah', value: 'UT' },
        { label: 'Vermont', value: 'VT' },
        { label: 'Virginia', value: 'VA' },
        { label: 'Washington', value: 'WA' },
        { label: 'West Virginia', value: 'WV' },
        { label: 'Wisconsin', value: 'WI' },
        { label: 'Wyoming', value: 'WY' }
      ]
    }
  },

  methods: {
    validateRoutingNumber(event: any) {
      const sanitized = event.target.value.replace(/\D/g, '')
      event.target.value = sanitized
      this.routingNumber = sanitized
    },
    validateAccountNumber(event: any) {
      const sanitized = event.target.value.replace(/\D/g, '').slice(0, 17)
      // Setting event.target.value = sanitized directly updates the input so invalid characters are removed immediately,
      // even if Vue doesn’t re-render
      event.target.value = sanitized
      this.accountNumber = sanitized
    },
    save() {
      if (this.hasErrors()) {
        return
      }
      const payload = {
        district_id: this.districtId,
        routing_number: this.routingNumber,
        account_number: this.accountNumber,
        account_type: this.selecedAccountType,
        environment_id: this.environmentId,
        billing_address: {
          given_name: this.userParameters?.firstName,
          surname: this.userParameters?.lastName,
          address_1: this.userParameters?.address1,
          city: this.userParameters?.city,
          region: this.userParameters?.state,
          postal_code:
            this.userParameters?.zipCode != null ? String(this.userParameters.zipCode) : '',
          country: 'US'
        }
      }
      const savePromise = this.saveHandler
        ? this.saveHandler(payload)
        : API.Post.vericheckData(payload)
      savePromise
        .then(() => {
          this.$toast.add({
            severity: 'success',
            summary: this.$t('c.fundingForms.vericheckDialog.success'),
            detail: this.$t('c.fundingForms.vericheckDialog.savedSuccessfully'),
            life: 6000
          })
          this.$emit('update:modelValue', false)
          this.$emit('funding-info-saved')
        })
        .catch(err => {
          console.error(err)
          this.$toast.add({
            severity: 'error',
            summary: this.$t('c.fundingForms.vericheckDialog.error'),
            detail: this.$t('c.fundingForms.vericheckDialog.saveFailed'),
            life: 6000
          })
        })
    },
    getUserData() {
      if (this.initialUserData) {
        this.userParameters = { ...this.initialUserData }
        return
      }
      API.Get.userData()
        .then(results => {
          this.userParameters = results
        })
        .catch(err => {
          console.error(err)
        })
    },
    hasErrors() {
      let hasError = false
      if (!this.accountNumber) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('c.fundingForms.vericheckDialog.error'),
          detail: this.$t('c.fundingForms.vericheckDialog.accountNumberRequired'),
          life: 6000
        })
        hasError = true
      }
      if (!this.routingNumber) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('c.fundingForms.vericheckDialog.error'),
          detail: this.$t('c.fundingForms.vericheckDialog.routingNumberRequired'),
          life: 6000
        })
        hasError = true
      }
      if (!this.userParameters?.zipCode) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('c.fundingForms.vericheckDialog.error'),
          detail: this.$t('c.fundingForms.vericheckDialog.zipCodeRequired'),
          life: 6000
        })
        hasError = true
      }
      if (!this.userParameters?.zipCode?.toString().match(/^\d{5}$/)) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('c.fundingForms.vericheckDialog.error'),
          detail: this.$t('c.fundingForms.vericheckDialog.zipCodeInvalid'),
          life: 6000
        })
        hasError = true
      }
      if (
        this.userParameters?.zipCode &&
        (this.userParameters?.zipCode.toString().length < 5 ||
          this.userParameters?.zipCode.toString().length > 5)
      ) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('c.fundingForms.vericheckDialog.error'),
          detail: this.$t('c.fundingForms.vericheckDialog.zipCodeFiveDigits'),
          life: 6000
        })
        hasError = true
      }
      return hasError
    }
  },

  mounted() {
    this.accountTypes = [
      { label: this.$t('c.fundingForms.vericheckDialog.checkingAccountLabel'), value: 'checking' },
      { label: this.$t('c.fundingForms.vericheckDialog.savingsAccountLabel'), value: 'saving' }
    ]
  },

  watch: {
    modelValue(newVal) {
      if (newVal) {
        this.getUserData()
      }
    }
  }
}
</script>

<style>
.dialog-container {
  padding-left: 1.25rem;
}
</style>
